/**
 * Admin Trade Execution Tab
 * 
 * Provides admin interface to execute trade entry/exit scripts directly from UI.
 * Allows entering first recommendation from queue, exiting positions via signals,
 * and searching/deleting trades by symbol.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import { logger } from "@app/services/logger";
import {
  executeEntryFromQueue,
  executeExitFromPosition,
  searchPositions,
  deleteTrade,
  type ScriptExecutionResult,
  type PositionSearchItem,
} from "@app/services/adminTradeExecutionApi";

import "./AdminTradeExecutionTab.css";

export function AdminTradeExecutionTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [symbol, setSymbol] = useState("");
  const [statusMessage, setStatusMessage] = useState<{type: 'success' | 'error', message: string} | null>(null);

  // --- Delete Trade State ---
  const [deleteSymbol, setDeleteSymbol] = useState("");
  const [searchResults, setSearchResults] = useState<PositionSearchItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const thStyle: React.CSSProperties = {
    padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: "0.75rem",
    borderBottom: "1px solid rgba(148,163,184,0.2)", whiteSpace: "nowrap",
  };
  const tdStyle: React.CSSProperties = {
    padding: "8px 12px", color: "#f1f5f9", borderBottom: "1px solid rgba(148,163,184,0.06)",
  };

  // Entry mutation
  const entryMutation = useMutation<ScriptExecutionResult, Error, string | undefined>({
    mutationFn: executeEntryFromQueue,
    onSuccess: (data) => {
      logger.info("Entry script executed successfully:", data as unknown as Record<string, unknown>);
      setStatusMessage({ type: 'success', message: data.message || "Entry signal queued successfully" });
      // Refresh positions and recommendations
      queryClient.invalidateQueries({ queryKey: ["active-positions"] });
      queryClient.invalidateQueries({ queryKey: ["recommendations"] });
    },
    onError: (error) => {
      logger.error("Entry script failed:", error);
      setStatusMessage({ type: 'error', message: error.message || "Failed to execute entry script" });
    },
  });

  // Exit mutation
  const exitMutation = useMutation<ScriptExecutionResult, Error, string | undefined>({
    mutationFn: executeExitFromPosition,
    onSuccess: (data) => {
      logger.info("Exit script executed successfully:", data as unknown as Record<string, unknown>);
      setStatusMessage({ type: 'success', message: data.message || "Exit signal emitted successfully" });
      // Refresh positions
      queryClient.invalidateQueries({ queryKey: ["active-positions"] });
    },
    onError: (error) => {
      logger.error("Exit script failed:", error);
      setStatusMessage({ type: 'error', message: error.message || "Failed to execute exit script" });
    },
  });

  const handleEntry = () => {
    const symbolToUse = symbol.trim() || undefined;
    entryMutation.mutate(symbolToUse);
  };

  const handleExit = () => {
    const symbolToUse = symbol.trim() || undefined;
    exitMutation.mutate(symbolToUse);
  };

  // --- Delete Trade Handlers ---

  const handleSearch = async () => {
    const sym = deleteSymbol.trim();
    if (!sym) return;
    setSearchLoading(true);
    setSearchResults([]);
    setDeleteMessage(null);
    try {
      const data = await searchPositions(sym, 20);
      setSearchResults(data.positions);
      if (data.positions.length === 0) {
        setDeleteMessage({ type: "error", message: `No closed positions found for "${sym}"` });
      }
    } catch (err) {
      setDeleteMessage({ type: "error", message: err instanceof Error ? err.message : "Search failed" });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleDelete = async (positionId: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete position ${positionId}?\n\nThis will remove the trade from user_positions and executed_trade_history. This action is IRREVERSIBLE.`
    );
    if (!confirmed) return;

    setDeletingId(positionId);
    setDeleteMessage(null);
    try {
      const result = await deleteTrade(positionId);
      if (result.success) {
        setDeleteMessage({ type: "success", message: result.message });
        // Remove from local results
        setSearchResults(prev => prev.filter(p => p.position_id !== positionId));
        // Refresh positions
        queryClient.invalidateQueries({ queryKey: ["active-positions"] });
      } else {
        setDeleteMessage({ type: "error", message: result.message });
      }
    } catch (err) {
      setDeleteMessage({ type: "error", message: err instanceof Error ? err.message : "Delete failed" });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="admin-trade-execution-root">
      <SectionHeader
        title="Trade Execution Control"
        subtitle="Execute entry and exit trade operations directly from the admin panel"
      />

      <Card className="admin-trade-execution-card">
        <div className="admin-trade-execution-section">
          <h3 className="admin-trade-execution-section-title">Symbol Filter (Optional)</h3>
          <div className="admin-trade-execution-input-group">
            <input
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              placeholder="e.g., BTCUSDT or leave empty for first available"
              className="admin-trade-execution-input"
            />
            <span className="admin-trade-execution-hint">
              Leave empty to use first available recommendation/position
            </span>
          </div>
        </div>

        <div className="admin-trade-execution-actions">
          {/* Entry Section */}
          <div className="admin-trade-execution-action-card">
            <div className="admin-trade-execution-action-header">
              <h4 className="admin-trade-execution-action-title">Enter Trade</h4>
              <span className="admin-trade-execution-action-badge entry">
                Entry
              </span>
            </div>
            <p className="admin-trade-execution-action-description">
              Execute the first recommendation from the queue (Active Pool → Smart Pool → Reserve Pool).
              Injects the trade as if it passed Live Gate + Sniper validation.
            </p>
            <div className="admin-trade-execution-action-details">
              <ul className="admin-trade-execution-action-steps">
                <li>Fetches recommendations from queue</li>
                <li>Converts to recommendation_data format</li>
                <li>Queues approval job for admin execution</li>
                <li>Removes from pool after enqueue</li>
              </ul>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={handleEntry}
              disabled={entryMutation.isPending}
              className="admin-trade-execution-action-button entry"
            >
              {entryMutation.isPending ? "Executing..." : "Execute Entry"}
            </Button>
            {entryMutation.isSuccess && entryMutation.data && (
              <div className="admin-trade-execution-result success">
                <strong>Success:</strong> {entryMutation.data.message}
              </div>
            )}
          </div>

          {/* Exit Section */}
          <div className="admin-trade-execution-action-card">
            <div className="admin-trade-execution-action-header">
              <h4 className="admin-trade-execution-action-title">Exit Trade</h4>
              <span className="admin-trade-execution-action-badge exit">
                Exit
              </span>
            </div>
            <p className="admin-trade-execution-action-description">
              Execute exit signal for an active position. Prefers admin positions.
              Emits exit signal for subscribers via signal dispatcher.
            </p>
            <div className="admin-trade-execution-action-details">
              <ul className="admin-trade-execution-action-steps">
                <li>Fetches active positions (admin preferred)</li>
                <li>Creates exit signal with mark_price</li>
                <li>Emits signal via RECONCILE_MANUAL source</li>
                <li>Dispatches to subscribers automatically</li>
              </ul>
            </div>
            <Button
              type="button"
              variant="danger"
              onClick={handleExit}
              disabled={exitMutation.isPending}
              className="admin-trade-execution-action-button exit"
            >
              {exitMutation.isPending ? "Executing..." : "Execute Exit"}
            </Button>
            {exitMutation.isSuccess && exitMutation.data && (
              <div className="admin-trade-execution-result success">
                <strong>Success:</strong> {exitMutation.data.message}
              </div>
            )}
          </div>
        </div>

        {/* ── Delete Trade Section ── */}
        <div className="admin-trade-execution-section">
          <h3 className="admin-trade-execution-section-title">
            🗑️ Delete Trade
          </h3>
          <p className="admin-trade-execution-action-description" style={{ marginBottom: "1rem" }}>
            Search for closed trades by symbol, then permanently delete them from the system.
            This removes the trade from user_positions and executed_trade_history. Irreversible.
          </p>

          <div className="admin-trade-execution-input-group">
            <div style={{ display: "flex", gap: "8px", maxWidth: "500px" }}>
              <input
                type="text"
                value={deleteSymbol}
                onChange={(e) => setDeleteSymbol(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                placeholder="e.g. TIA/USDT or TIAUSDT"
                className="admin-trade-execution-input"
                style={{ flex: 1 }}
              />
              <Button
                type="button"
                variant="primary"
                onClick={handleSearch}
                disabled={searchLoading || !deleteSymbol.trim()}
              >
                {searchLoading ? "Searching..." : "Search"}
              </Button>
            </div>
          </div>

          {searchResults.length > 0 && (
            <div className="admin-trade-delete-results" style={{ marginTop: "1rem" }}>
              <p style={{ color: "var(--text-secondary, #666)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                Found {searchResults.length} closed position(s) for <strong>{deleteSymbol.toUpperCase()}</strong>:
              </p>
              <div className="admin-trade-delete-table-wrap" style={{ overflowX: "auto" }}>
                <table className="admin-trade-delete-table" style={{
                  width: "100%", borderCollapse: "collapse", fontSize: "0.82rem",
                }}>
                  <thead>
                    <tr style={{ background: "rgba(15,23,42,0.6)", color: "rgba(203,213,225,0.7)" }}>
                      <th style={thStyle}>Symbol</th>
                      <th style={thStyle}>Position ID</th>
                      <th style={thStyle}>Entry</th>
                      <th style={thStyle}>Exit</th>
                      <th style={thStyle}>Exit Reason</th>
                      <th style={thStyle}>Closed</th>
                      <th style={thStyle}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((pos) => (
                      <tr key={pos.position_id} style={{
                        borderBottom: "1px solid rgba(148,163,184,0.1)",
                      }}>
                        <td style={tdStyle}>{pos.symbol}</td>
                        <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "0.72rem", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {pos.position_id}
                        </td>
                        <td style={tdStyle}>{pos.entry_price != null ? pos.entry_price.toFixed(6) : "-"}</td>
                        <td style={tdStyle}>{pos.exit_price != null ? pos.exit_price.toFixed(6) : "-"}</td>
                        <td style={{ ...tdStyle, maxWidth: "250px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {pos.exit_reason || "-"}
                        </td>
                        <td style={tdStyle}>
                          {pos.closed_at
                            ? new Date(pos.closed_at).toLocaleString("ar-SA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "-"}
                        </td>
                        <td style={tdStyle}>
                          <Button
                            type="button"
                            variant="danger"
                            size="small"
                            disabled={deletingId === pos.position_id}
                            onClick={() => handleDelete(pos.position_id)}
                          >
                            {deletingId === pos.position_id ? "Deleting..." : "Delete"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {searchResults.length === 0 && deleteMessage && (
            <div className={`admin-trade-execution-result ${deleteMessage.type}`} style={{ marginTop: "1rem" }}>
              <strong>{deleteMessage.type === 'success' ? 'Info:' : 'Error:'}</strong> {deleteMessage.message}
            </div>
          )}

          {deleteMessage && deletingId === null && (
            <div className={`admin-trade-execution-result ${deleteMessage.type}`} style={{ marginTop: "1rem" }}>
              <strong>{deleteMessage.type === 'success' ? 'Success:' : 'Error:'}</strong> {deleteMessage.message}
            </div>
          )}
        </div>

        {statusMessage && (
          <div className={`admin-trade-execution-result ${statusMessage.type}`}>
            <strong>{statusMessage.type === 'success' ? 'Success:' : 'Error:'}</strong> {statusMessage.message}
          </div>
        )}

        <div className="admin-trade-execution-warning">
          <strong>⚠️ Warning:</strong> These operations execute real trades through the system.
          Make sure you understand the impact before proceeding. The backend worker must be running
          for entry operations to complete.
        </div>
      </Card>
    </div>
  );
}
